import * as React from "react"

import { cn } from "@/lib/utils"

type HTMLHeadingProps = React.HTMLAttributes<HTMLHeadingElement>
type HTMLParagraphProps = React.HTMLAttributes<HTMLParagraphElement>
type HTMLDivProps = React.HTMLAttributes<HTMLDivElement>
type HTMLQuoteProps = React.BlockquoteHTMLAttributes<HTMLQuoteElement>
type HTMLCodeProps = React.HTMLAttributes<HTMLElement>
type HTMLListProps = React.HTMLAttributes<HTMLUListElement>
type HTMLOListProps = React.OlHTMLAttributes<HTMLOListElement>
type HTMLTableProps = React.TableHTMLAttributes<HTMLTableElement>
type HTMLTableSectionProps = React.HTMLAttributes<HTMLTableSectionElement>
type HTMLTableRowProps = React.HTMLAttributes<HTMLTableRowElement>
type HTMLTableCellProps = React.ThHTMLAttributes<HTMLTableCellElement>
type HTMLTableDataCellProps = React.TdHTMLAttributes<HTMLTableCellElement>
type HTMLSmallProps = React.HTMLAttributes<HTMLElement>
type HTMLListItemProps = React.LiHTMLAttributes<HTMLLIElement>

type TypographyVariant =
  | "h1"
  | "h2"
  | "h3"
  | "h4"
  | "p"
  | "blockquote"
  | "inlineCode"
  | "lead"
  | "large"
  | "small"
  | "muted"

type TypographyElementMap = {
  h1: HTMLHeadingElement
  h2: HTMLHeadingElement
  h3: HTMLHeadingElement
  h4: HTMLHeadingElement
  p: HTMLParagraphElement
  blockquote: HTMLQuoteElement
  inlineCode: HTMLElement
  lead: HTMLParagraphElement
  large: HTMLDivElement
  small: HTMLElement
  muted: HTMLParagraphElement
}

type TypographyProps<T extends TypographyVariant> = {
  as?: T
  className?: string
} & React.HTMLAttributes<TypographyElementMap[T]>

const typographyConfig = {
  h1: {
    component: "h1",
    className:
      "font-sans scroll-m-20 text-4xl font-extrabold tracking-tight text-balance",
  },
  h2: {
    component: "h2",
    className:
      "font-sans scroll-m-20 border-b pb-2 text-3xl font-semibold tracking-tight first:mt-0",
  },
  h3: {
    component: "h3",
    className: "font-sans scroll-m-20 text-2xl font-semibold tracking-tight",
  },
  h4: {
    component: "h4",
    className: "font-sans scroll-m-20 text-xl font-semibold tracking-tight",
  },
  p: {
    component: "p",
    className: "font-sans leading-7 not-first:mt-6",
  },
  blockquote: {
    component: "blockquote",
    className: "font-sans mt-6 border-l-2 pl-6 italic",
  },
  inlineCode: {
    component: "code",
    className:
      "relative rounded bg-muted px-[0.3rem] py-[0.2rem] font-mono text-sm font-semibold",
  },
  lead: {
    component: "p",
    className: "font-sans text-xl text-muted-foreground",
  },
  large: {
    component: "div",
    className: "font-sans text-lg font-semibold",
  },
  small: {
    component: "small",
    className: "font-sans text-sm leading-none font-medium",
  },
  muted: {
    component: "p",
    className: "font-sans text-sm text-muted-foreground",
  },
} as const satisfies Record<
  TypographyVariant,
  { component: keyof React.JSX.IntrinsicElements; className: string }
>

const Typography = React.forwardRef(
  <T extends TypographyVariant = "p">(
    { as, className, ...props }: TypographyProps<T>,
    ref: React.ForwardedRef<TypographyElementMap[T]>,
  ) => {
    const variant = as ?? "p"
    const config = typographyConfig[variant]
    const Component = config.component
    const elementProps = props as React.HTMLAttributes<HTMLElement>

    return (
      <Component
        ref={ref as never}
        className={cn(config.className, className)}
        {...elementProps}
      />
    )
  },
) as <T extends TypographyVariant = "p">(
  props: TypographyProps<T> & {
    ref?: React.ForwardedRef<TypographyElementMap[T]>
  },
) => React.ReactElement | null

const TypographyH1 = React.forwardRef<HTMLHeadingElement, HTMLHeadingProps>(
  (props, ref) => <Typography as="h1" ref={ref} {...props} />,
)
TypographyH1.displayName = "TypographyH1"

const TypographyH2 = React.forwardRef<HTMLHeadingElement, HTMLHeadingProps>(
  (props, ref) => <Typography as="h2" ref={ref} {...props} />,
)
TypographyH2.displayName = "TypographyH2"

const TypographyH3 = React.forwardRef<HTMLHeadingElement, HTMLHeadingProps>(
  (props, ref) => <Typography as="h3" ref={ref} {...props} />,
)
TypographyH3.displayName = "TypographyH3"

const TypographyH4 = React.forwardRef<HTMLHeadingElement, HTMLHeadingProps>(
  (props, ref) => <Typography as="h4" ref={ref} {...props} />,
)
TypographyH4.displayName = "TypographyH4"

const TypographyP = React.forwardRef<HTMLParagraphElement, HTMLParagraphProps>(
  (props, ref) => <Typography as="p" ref={ref} {...props} />,
)
TypographyP.displayName = "TypographyP"

const TypographyLead = React.forwardRef<
  HTMLParagraphElement,
  HTMLParagraphProps
>((props, ref) => <Typography as="lead" ref={ref} {...props} />)
TypographyLead.displayName = "TypographyLead"

const TypographyLarge = React.forwardRef<HTMLDivElement, HTMLDivProps>(
  (props, ref) => <Typography as="large" ref={ref} {...props} />,
)
TypographyLarge.displayName = "TypographyLarge"

const TypographySmall = React.forwardRef<HTMLElement, HTMLSmallProps>(
  (props, ref) => <Typography as="small" ref={ref} {...props} />,
)
TypographySmall.displayName = "TypographySmall"

const TypographyMuted = React.forwardRef<
  HTMLParagraphElement,
  HTMLParagraphProps
>((props, ref) => <Typography as="muted" ref={ref} {...props} />)
TypographyMuted.displayName = "TypographyMuted"

const TypographyBlockquote = React.forwardRef<HTMLQuoteElement, HTMLQuoteProps>(
  (props, ref) => <Typography as="blockquote" ref={ref} {...props} />,
)
TypographyBlockquote.displayName = "TypographyBlockquote"

const TypographyInlineCode = React.forwardRef<HTMLElement, HTMLCodeProps>(
  (props, ref) => <Typography as="inlineCode" ref={ref} {...props} />,
)
TypographyInlineCode.displayName = "TypographyInlineCode"

interface TypographyListProps extends HTMLListProps {
  ordered?: false
}

interface TypographyOrderedListProps extends HTMLOListProps {
  ordered: true
}

function TypographyListBase(
  {
    className,
    ordered,
    ...props
  }: TypographyListProps | TypographyOrderedListProps,
  ref: React.ForwardedRef<HTMLUListElement | HTMLOListElement>,
) {
  const Component = ordered ? "ol" : "ul"

  return (
    <Component
      ref={ref as never}
      className={cn(
        ordered
          ? "my-6 ml-6 list-decimal [&>li]:mt-2"
          : "my-6 ml-6 list-disc [&>li]:mt-2",
        className,
      )}
      {...props}
    />
  )
}

const TypographyList = React.forwardRef(TypographyListBase)
TypographyList.displayName = "TypographyList"

const TypographyListItem = React.forwardRef<HTMLLIElement, HTMLListItemProps>(
  ({ className, ...props }, ref) => (
    <li ref={ref} className={cn(className)} {...props} />
  ),
)
TypographyListItem.displayName = "TypographyListItem"

const TypographyTable = React.forwardRef<HTMLTableElement, HTMLTableProps>(
  ({ className, ...props }, ref) => (
    <div className="my-6 w-full overflow-y-auto">
      <table ref={ref} className={cn("w-full", className)} {...props} />
    </div>
  ),
)
TypographyTable.displayName = "TypographyTable"

const TypographyTableHeader = React.forwardRef<
  HTMLTableSectionElement,
  HTMLTableSectionProps
>(({ className, ...props }, ref) => (
  <thead ref={ref} className={cn("[&_tr]:border-b", className)} {...props} />
))
TypographyTableHeader.displayName = "TypographyTableHeader"

const TypographyTableBody = React.forwardRef<
  HTMLTableSectionElement,
  HTMLTableSectionProps
>(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    className={cn("[&_tr:last-child]:border-0", className)}
    {...props}
  />
))
TypographyTableBody.displayName = "TypographyTableBody"

const TypographyTableRow = React.forwardRef<
  HTMLTableRowElement,
  HTMLTableRowProps
>(({ className, ...props }, ref) => (
  <tr
    ref={ref}
    className={cn("m-0 border-t p-0 even:bg-muted", className)}
    {...props}
  />
))
TypographyTableRow.displayName = "TypographyTableRow"

const TypographyTableHead = React.forwardRef<
  HTMLTableCellElement,
  HTMLTableCellProps
>(({ className, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      "border px-4 py-2 text-left font-bold [[align=center]]:text-center [[align=right]]:text-right",
      className,
    )}
    {...props}
  />
))
TypographyTableHead.displayName = "TypographyTableHead"

const TypographyTableCell = React.forwardRef<
  HTMLTableCellElement,
  HTMLTableDataCellProps
>(({ className, ...props }, ref) => (
  <td
    ref={ref}
    className={cn(
      "border px-4 py-2 text-left [[align=center]]:text-center [[align=right]]:text-right",
      className,
    )}
    {...props}
  />
))
TypographyTableCell.displayName = "TypographyTableCell"

export {
  Typography,
  TypographyBlockquote,
  TypographyH1,
  TypographyH2,
  TypographyH3,
  TypographyH4,
  TypographyInlineCode,
  TypographyLarge,
  TypographyLead,
  TypographyList,
  TypographyListItem,
  TypographyMuted,
  TypographyP,
  TypographySmall,
  TypographyTable,
  TypographyTableBody,
  TypographyTableCell,
  TypographyTableHead,
  TypographyTableHeader,
  TypographyTableRow,
}
